/* Shop Collection Row — arrow navigation for a horizontal product carousel.
 *
 * Pairs with sections/shop-collection-row.liquid. The track is a native
 * overflow-x scroll container with scroll-snap, so touch/drag scrolling is
 * free; this component only adds discrete prev/next paging via arrow buttons
 * and toggles their disabled state at the track ends.
 *
 * The product cards inside are `block.landing-picker-card` snippets wired up
 * separately by landing-product-picker.js (variant/frequency/add-to-cart).
 * This component is intentionally independent of that logic.
 */

class ShopCollectionRow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]')
    this.prevBtn = this.querySelector('[data-prev]')
    this.nextBtn = this.querySelector('[data-next]')
    if (!this.track) return

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.page(-1))
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.page(1))

    this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true })
    window.addEventListener('resize', () => {
      this.updateArrows()
      this.measureSizes()
    })

    this.cards = Array.from(this.querySelectorAll('.shop-card'))
    this.cards.forEach((card) => this.wireSizeMirror(card))

    // Defer first measurement until layout settles (images, fonts).
    requestAnimationFrame(() => {
      this.updateArrows()
      this.measureSizes()
    })
    // Fonts change pill widths → re-measure once they're ready.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.measureSizes())
    }
  }

  // Size pills collapse into a dropdown once they wrap past two rows. We can't
  // detect line count in CSS (wrapping depends on each label's text width), so
  // we measure the rendered height here. The radios stay the source of truth;
  // the mirror <select> just drives them, so landing-product-picker.js is
  // untouched.
  wireSizeMirror(card) {
    const fieldset = card.querySelector('[data-size-pills]')
    const select = card.querySelector('[data-size-mirror]')
    if (!fieldset || !select) return

    // select → radio (so the shared picker JS reacts as if a pill was clicked)
    select.addEventListener('change', () => {
      const radio = fieldset.querySelector(`input[value="${select.value}"]`)
      if (radio && !radio.disabled) {
        radio.checked = true
        radio.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })

    // radio → select (keep the dropdown in sync if selection changes elsewhere)
    fieldset.addEventListener('change', (e) => {
      const radio = e.target.closest('input[data-size-radio]')
      if (radio) select.value = radio.value
    })
  }

  measureSizes() {
    if (!this.cards) return
    this.cards.forEach((card) => this.measureSize(card))
  }

  measureSize(card) {
    const fieldset = card.querySelector('[data-size-pills]')
    if (!fieldset) return
    const pills = fieldset.querySelectorAll('.landing-picker__sizes-pill')
    if (pills.length < 2) {
      card.removeAttribute('data-size-collapsed')
      return
    }
    // Measure the pills in their natural (un-collapsed) layout, otherwise a
    // collapsed card reads height 0 and would oscillate back to pills.
    card.removeAttribute('data-size-collapsed')
    const rowHeight = pills[0].offsetHeight
    if (!rowHeight) return
    const twoRows = rowHeight * 2 + 12 // tolerance for one row gap
    card.toggleAttribute('data-size-collapsed', fieldset.scrollHeight > twoRows)
  }

  // One "page" = the width of a single card plus the track gap, so the
  // snap lands cleanly on the next card.
  step() {
    const card = this.track.querySelector('.landing-picker__column')
    if (!card) return this.track.clientWidth
    const styles = getComputedStyle(this.track)
    const gap = parseFloat(styles.columnGap || styles.gap) || 0
    return card.getBoundingClientRect().width + gap
  }

  page(direction) {
    this.track.scrollBy({ left: direction * this.step(), behavior: 'smooth' })
  }

  updateArrows() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth - 1
    const hasOverflow = maxScroll > 1
    const atStart = this.track.scrollLeft <= 1
    const atEnd = this.track.scrollLeft >= maxScroll

    if (this.prevBtn) this.prevBtn.disabled = !hasOverflow || atStart
    if (this.nextBtn) this.nextBtn.disabled = !hasOverflow || atEnd

    // Lets the section hide the whole nav when everything already fits.
    this.toggleAttribute('data-no-overflow', !hasOverflow)
  }
}

customElements.define('shop-collection-row', ShopCollectionRow)

export { ShopCollectionRow }
